import React, { useEffect, useState, useContext } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import { uuid, getQuoteType, getProductLocations, getObjectLabels } from '@paintscout/util/builder'
import type { ProductOption, BulkEditDetails, BulkOperation } from '@paintscout/util/builder'
import type { QuoteDocument, OptionsDocument } from 'paintscout'
import {
  DialogStackContext,
  Tile,
  Typography,
  FormSectionTitle,
  Button,
  InputField,
  DropdownSelect
} from '@paintscout/ui'
import type { DropdownSelectOption } from '@paintscout/ui'
import ProductSelectDialog from '../ProductSelectDialog'

const useStyles = makeStyles((theme) => ({
  root: {},
  editItemSection: {
    alignItems: 'center',
    margin: '0.5em 0em 0.5em 0',
    backgroundColor: theme.paintscout.palette.brand.Neutral.P94
  },
  tile: {
    height: '4em',
    width: '14em',
    marginBottom: theme.spacing(1)
  },
  dropdown: {
    width: '9em',
    marginBottom: theme.spacing(0.5),
    color: 'white'
  },
  locationsGrid: {
    marginBottom: '1em'
  },
  input: {
    width: '4em'
  }
}))

export interface ProductSectionProps {
  product: string
  quote: QuoteDocument
  options: OptionsDocument
  bulkDetails: BulkEditDetails
  handleProductChange: (operation: BulkOperation) => any
  checkParentHasChanges?: () => boolean
}

function ProductSection(props: ProductSectionProps) {
  const { product, bulkDetails, options, quote, handleProductChange } = props
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const classes = useStyles(props)
  const [operation, setOperation] = useState<BulkOperation>({ key: uuid(), oldProduct: product })
  //get items that use product
  const productLocations = getProductLocations(product, bulkDetails)
  const objectLabels = getObjectLabels({ options })
  const [selectedOption, setSelectedOption] = useState(null)
  const hasLineItems = productLocations.some((location) => location.type === 'line-item')
  const hasSubstrates = productLocations.some((location) => location.type === 'substrate')
  const updateOptions = [
    hasLineItems && hasSubstrates ? { value: 'all', label: 'All' } : null,
    hasLineItems ? { value: 'line-items', label: 'Line Items' } : null,
    hasSubstrates ? { value: 'substrates', label: objectLabels.substrate.plural } : null
  ].filter(Boolean)

  // Pass our updated operation to our outer list on update if started
  useEffect(() => {
    if (operation.lineItems || operation.substrates || operation.newProduct) {
      handleProductChange(operation)
    }
  }, [operation])

  // Open dialog for material choice, once selected add to operation
  const handleChangeMaterial = () => {
    return openDialog(ProductSelectDialog, {
      options,
      checkParentHasChanges: props.checkParentHasChanges,
      onConfirm: (productOption: ProductOption, _custom?: boolean) => {
        const newOperation = { ...operation, newProduct: productOption }
        setOperation(newOperation)
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      },
      quoteType: getQuoteType({ options, quote })
    })
  }

  const handleChangeTargets = (item: DropdownSelectOption) => {
    const lineItems = []
    const substrates = []
    const selectedValue = updateOptions.find((option) => option.value === item.value)
    setSelectedOption(selectedValue)
    if (item.value === 'all') {
      // if all, set lineItems to all lineItems, substrates to all substrates
      productLocations.forEach((location) => {
        if (location?.type === 'line-item') {
          lineItems.push(location)
        } else if (location?.type === 'substrate') {
          substrates.push(location)
        }
      })
      const newOperation = { ...operation, lineItems, substrates }
      setOperation(newOperation)
    } else if (item.value === 'line-items') {
      // if line-items, add all lineItems, clear substrates to blank

      productLocations.forEach((location) => (location.type === 'line-item' ? lineItems.push(location) : null))

      const newOperation = { ...operation, lineItems, substrates: [] }
      setOperation(newOperation)
    } else if (item.value === 'substrates') {
      // if substrates, add all substrates, clear lineitems to blank

      productLocations.forEach((location) => (location.type === 'substrate' ? substrates.push(location) : null))

      const newOperation = { ...operation, lineItems: [], substrates }
      setOperation(newOperation)
    }
  }

  const handleClear = () => {
    setOperation({ key: operation.key, oldProduct: operation.oldProduct })
    setSelectedOption(null)
  }

  const handleChangeColorValue = (e: any) => {
    setOperation({
      ...operation,
      newProduct: {
        ...operation.newProduct,
        color: e.target.value
      }
    })
  }

  return (
    <Grid container={true} item={true} xs={12} className={classes.editItemSection}>
      <Grid item={true} xs={12} sm={7} className={classes.locationsGrid}>
        <FormSectionTitle title={product} variant={'h3'} />
        <FormSectionTitle title={'Locations:'} variant={'h4'} />
        {productLocations.filter(Boolean).map((location, i) => {
          return (
            <>
              {location?.type === 'substrate' && (
                <div key={location.name + `${i}`}>
                  <Typography variant="subtitle2" gutterBottom={false}>
                    -{location.type}-
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom={false}>
                    {location.name} - {location.substrate.rate.label}
                  </Typography>
                </div>
              )}
              {location?.type === 'line-item' && (
                <div key={location.name + `${i}`}>
                  <Typography variant="subtitle2" gutterBottom={false}>
                    -{location.type}-
                  </Typography>
                  <Typography variant="subtitle1" gutterBottom={false}>
                    {location.name}
                  </Typography>
                </div>
              )}
            </>
          )
        })}
      </Grid>
      <Grid item={true} xs={12} sm={5}>
        <FormSectionTitle title={'New Material'} variant={'h5'} />
        <Tile onClick={() => handleChangeMaterial()} className={classes.tile}>
          {operation.newProduct && <label>{operation.newProduct?.label}</label>}
          {!operation.newProduct && <label>Select...</label>}
        </Tile>
        <DropdownSelect
          label={'Apply To'}
          variant="single"
          options={updateOptions}
          className={classes.dropdown}
          value={selectedOption}
          onChange={(item: DropdownSelectOption) => handleChangeTargets(item)}
        />
        {operation.newProduct && selectedOption?.value && selectedOption.value !== 'line-items' && (
          <InputField
            value={operation?.newProduct?.color}
            className={classes.input}
            name={'color'}
            type={'string'}
            label={'Color'}
            onChange={(e: any) => handleChangeColorValue(e)}
          />
        )}
      </Grid>
      {(operation.lineItems || operation.substrates || operation.newProduct) && (
        <Grid item={true} xs={12} sm={5}>
          <Button onClick={handleClear}>Clear</Button>
        </Grid>
      )}
    </Grid>
  )
}

export default ProductSection
